<template>
  <div v-if="navConfig" class="tab-nav tab-nav--main pl-3">
    <!-- Home Icon, Title and Diviver -->
    <div
      v-if="navConfig.homeRoute"
      class="tab-nav__home"
      @click="onHomeClick"
    >
      <i :class="navConfig.homeIcon || 'fa-duotone fa-solid fa-house'" />
    </div>
    <div v-if="navConfig.includeTitle" class="tab-nav__title-container">
      <span class="tab-nav__title-text" :title="$rsGlobals.$sidebarHeaderData.title">
        {{ $rsGlobals.$sidebarHeaderData.title }}
      </span>
    </div>
    <div v-if="navConfig.includeTitle" class="tab-nav__divider" />

    <!-- Navigation Tabs -->
    <div class="tab-nav__wrap">
      <div
        v-for="item in visibleTabs"
        :key="item.route"
        :ref="`tab-${item.route}`"
        class="tab-nav__item"
        :class="{ 'tab-nav__item--active': isTabActive(item) }"
        @click="onTabClick(item, $event)"
      >
        <span class="tab-nav__title">
          {{ getTabLabel(item) }}
          <i
            v-if="hasChildren(item)"
            class="fa-solid fa-chevron-down rs-iframe-nav-bar__chevron"
            :class="{ 'rs-iframe-nav-bar__chevron--open': openDropdown === item.route }"
          />
        </span>

        <div
          v-if="hasChildren(item) && openDropdown === item.route"
          class="rs-iframe-nav-bar__dropdown"
        >
          <div
            v-for="child in item.children"
            :key="child.route"
            class="rs-iframe-nav-bar__dropdown-item"
            :class="{ 'rs-iframe-nav-bar__dropdown-item--active': $route.name === child.route }"
            @click.stop="onChildClick(child)"
          >
            {{ getTabLabel(child) }}
          </div>
        </div>
      </div>

      <div class="tab-nav__slider">
        <div
          class="indicator"
          :style="{ transform: `translateX(${indicatorStyle.left}px)`, width: `${indicatorStyle.width}px` }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { IFrameNav, IFrameNavAccessConfig, IFrameNavTab } from '@/shared';
import { getIFrameNavTabLabel } from '@/shared/helpers/route-meta-translations';

type IndicatorStyle = { left: number; width: number };

export default defineComponent({
  name: 'RsIframeNavBar',

  props: {
    navConfig: {
      type: Object as PropType<IFrameNav | null>,
      default: null,
    },
  },

  data() {
    return {
      openDropdown: null as string | null,
      indicatorStyle: { left: 0, width: 0 } as IndicatorStyle,
    };
  },

  computed: {
    /**
     * Returns tabs the current user has permission to see, with inaccessible
     * children filtered out. A parent tab is shown if it or any child is accessible.
     */
    visibleTabs(): IFrameNavTab[] {
      if (!this.navConfig) return [];

      const hasAccess = (access: IFrameNavAccessConfig): boolean => {
        const { key } = access;
        const check = (k: string) => this.$rsGlobals.$userHasAccess({ ...access, key: k });
        return Array.isArray(key) ? key.some(check) : check(key);
      };

      return this.navConfig.tabs
        .map((tab) => {
          const visibleChildren = tab.children?.filter((child) => hasAccess(child.access)) || [];
          return { ...tab, children: visibleChildren };
        })
        .filter((tab) => hasAccess(tab.access) || tab.children.length > 0);
    },

    /**
     * Resolves the route name of the currently active tab, preferring an exact
     * route name match before falling back to an `activePath` URL substring match.
     */
    activeTabRoute(): string | null {
      if (!this.navConfig) return null;

      const { tabs } = this.navConfig;
      const currentPath = this.$route.path.toLowerCase();

      const exactMatch = tabs.find((item) => item.route === this.$route.name
        || item.children?.some((c) => c.route === this.$route.name));
      if (exactMatch) return exactMatch.route ?? null;

      const activePathMatch = tabs.find((item) => {
        if (!item.activePath) return false;
        const paths = Array.isArray(item.activePath) ? item.activePath : [item.activePath];
        return paths.some((p) => currentPath.includes(p.toLowerCase()));
      });
      if (activePathMatch) return activePathMatch.route ?? null;

      return null;
    },
  },

  watch: {
    '$route.name': {
      handler() {
        this.openDropdown = null;
        this.$nextTick(() => this.syncIndicator());
      },
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$nextTick(() => this.syncIndicator());
    });
    document.addEventListener('click', this.onOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.onOutsideClick);
  },

  methods: {
    getTabLabel(tab: Pick<IFrameNavTab, 'label' | 'labelKey'>): string {
      return getIFrameNavTabLabel(this, tab);
    },

    /** Returns true if the tab has at least one visible child. */
    hasChildren(item: IFrameNavTab): boolean {
      return Array.isArray(item.children) && item.children.length > 0;
    },

    /** Returns true if the tab corresponds to the currently active route. */
    isTabActive(item: IFrameNavTab): boolean {
      return item.route === this.activeTabRoute;
    },

    /** Closes any dropdown and navigates to the home route. */
    onHomeClick(): void {
      this.openDropdown = null;
      if (this.navConfig?.homeRoute) {
        this.$router.push({ name: this.navConfig.homeRoute });
      }
    },

    /**
     * Measures the active tab element and updates the sliding indicator position
     * and width. Hides the indicator if no tab is active or the element is not found.
     */
    syncIndicator(): void {
      if (!this.activeTabRoute) {
        this.indicatorStyle = { left: 0, width: 0 };
        return;
      }

      const els = this.$refs[`tab-${this.activeTabRoute}`] as HTMLElement[] | HTMLElement | undefined;
      const el = Array.isArray(els) ? els[0] : els;

      this.indicatorStyle = el
        ? { left: el.offsetLeft, width: el.clientWidth }
        : { left: 0, width: 0 };
    },

    /** Toggles any dropdown for tabs with children, or navigates directly for leaf tabs. */
    onTabClick(item: IFrameNavTab, event: MouseEvent): void {
      if (this.hasChildren(item)) {
        event.stopPropagation();
        this.openDropdown = this.openDropdown === item.route ? null : item.route;
        return;
      }
      this.openDropdown = null;
      this.$router.push({ name: item.route });
    },

    /** Closes any dropdown and navigates to the selected child tab. */
    onChildClick(child: Omit<IFrameNavTab, 'children'>): void {
      this.openDropdown = null;
      this.$router.push({ name: child.route });
    },

    /** Closes any dropdown when a click is detected outside it. */
    onOutsideClick(): void {
      this.openDropdown = null;
    },
  },
});
</script>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.tab-nav__home {
  padding: 0 16px;
  cursor: pointer;
  font-size: 18px;
  color: $secondary-color-dark;

  &:hover {
    color: $secondary-color;
  }
}

.tab-nav__title-container {
  display: flex;
  align-items: center;
  flex-shrink: 1;
  min-width: 0;
}

.tab-nav__title-text {
  font-weight: 700;
  font-size: 14px;
  color: $secondary-color-dark;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  cursor: default;
}

.tab-nav__divider {
  width: 1px;
  height: 22px;
  background-color: $tertiary-gray;
  margin: 0 12px 0 20px;
  flex-shrink: 0;
}

.rs-iframe-nav-bar {
  &__chevron {
    margin-left: 4px;
    transition: transform 0.15s ease;
    font-size: 10px;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    background: $white;
    border: 1px solid $tertiary-gray;
    border-radius: 6px;
    min-width: 176px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    z-index: 500;
  }

  &__dropdown-item {
    padding: 9px 16px;
    font-size: rem-calc(14px);
    font-weight: 400;
    color: $tertiary-muted;
    white-space: nowrap;
    transition: background 0.1s ease, color 0.1s ease;

    &:hover {
        background: rgba($secondary-color, 0.05);
        color: $secondary-color;
      }

    &--active {
      font-weight: 700;
      color: $secondary-color;
      background: rgba($secondary-color, 0.1);
    }
  }
}
</style>
